<template>
  <div class="w-full">
    <div class="sticky top-0 z-10 flex items-center justify-between h-16 px-4 bg-white border-b lg:px-6">
      <h1 class="text-lg font-bold">
        View Booking
      </h1>
    </div>

    <div class="px-4 py-3 border-b lg:px-6 bg-gray-50">
      <router-link :to="{ name: 'CustomerBookingList' }" class="text-accent">
        &larr; Back to Bookings
      </router-link>
    </div>
    
    <div class="min-h-screen">
      <div class="grid px-6 mt-6 md:grid-cols-2 gap-y-4">
        <!-- Booking Details -->
        <div class="col-span-2 md:col-span-1">
          <div class="text-lg font-bold">
            Booking Details
          </div>
          
          <div class="py-4 space-y-4">
            <div>
              <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
                Status
              </div>
              <div>
                {{ booking.status | capitalize }}
              </div>
            </div>

            <div>
              <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
                Chosen Date
              </div>
              <div>
                {{ booking.date | moment('DD/MM/YYYY') || 'None' }}
              </div>
            </div>

            <div>
              <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
                Chosen Time
              </div>
              <div>
                {{ booking.time || 'None' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Customer Details -->
        <div class="col-span-2 md:col-span-1">
          <div class="text-lg font-bold">
            Your Details
          </div>

          <div class="py-4 space-y-4">
            <div>
              <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
                Name
              </div>
              <div>
                {{ booking.customer_name }}
              </div>
            </div>

            <div>
              <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
                Email
              </div>
              <div>
                {{ booking.customer_email }}
              </div>
            </div>

            <div>
              <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
                Phone Number
              </div>
              <div>
                {{ booking.customer_phone }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pickups -->
      <div class="mt-8 border-b">
        <div class="px-6 pb-4 text-lg font-bold">
          Pickups
        </div>
        <div>
          <template v-for="pickup in pickups">
            <div :key="pickup.id" class="cursor-pointer hover:bg-gray-50">
              <button class="grid items-start justify-between w-full px-6 py-4 text-left border-t gap-y-2 lg:grid-cols-12"
              @click="openDetails(pickup)">
                <div class="lg:col-span-6">
                  <div>
                    <div class="max-w-full truncate">
                      <span class="font-medium" v-if="pickup.contact_name">{{ pickup.contact_name }}</span>  
                      <span v-if="pickup.name"> &mdash; {{ pickup.name }}</span>
                    </div>
                  </div>
                  <div v-if="pickup.formatted_address" class="max-w-full text-gray-600 truncate">
                    {{ pickup.formatted_address }}
                  </div>
                  <div class="mt-2 md:mt-1 md:flex md:space-x-4">
                    <div v-if="pickup.contact_phone" class="flex items-center">
                      <fa :icon="['fas', 'phone']" fixed-width class="mr-1 text-xs opacity-50" /> {{ pickup.contact_phone }}
                    </div>
                    <div v-if="pickup.contact_email" class="flex items-center">
                      <fa :icon="['fas', 'envelope']" fixed-width class="mr-1 text-xs opacity-50" /> {{ pickup.contact_email }}
                    </div>
                    <div v-if="pickup.reference" class="flex items-center">
                      <fa :icon="['fas', 'hashtag']" fixed-width class="mr-1 text-xs opacity-50" /> {{ pickup.reference }}
                    </div>
                  </div>
                </div>
                <div class="grid gap-2 text-gray-500 lg:col-span-4">
                  <div>
                    <div v-for="product in pickup.products" :key="product.id" class="truncate">
                      {{ product.quantity }}x {{ product.name}}
                    </div>
                  </div>
                  <div>
                    <div v-for="addon in pickup.addons" :key="addon.id" class="truncate">
                      &check; {{ addon.name}}
                    </div>
                  </div>
                </div>

                <div v-if="pickup.status" class="flex items-center justify-end lg:col-span-2">
                  <div>
                    <span class="inline-block px-3 py-1 text-xs font-medium tracking-wide uppercase border rounded-full" :class="statusClass(pickup.status)">
                      {{ pickup.status | capitalize }}
                    </span>
                  </div>
                  <div class="ml-6" v-if="pickup.status">
                    <fa :icon="['far', 'chevron-down']" class="transition duration-200 transform" :class="{ 'rotate-180': showDetails === pickup.id}" />
                  </div>
                </div>
              </button>
              <div>
                <Collapse :active="showDetails === pickup.id">
                  <div class="px-6 pb-4 space-y-4">
                    <div v-if="pickup.notes">
                      <span class="font-medium">Notes:</span> {{ pickup.notes || 'No Notes' }}
                    </div>
                    <div class="grid grid-cols-4 gap-4 p-6 text-sm font-medium leading-tight text-center rounded bg-gray-50">
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('captured', pickup.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('captured', pickup.id)" />
                        </div>
                        <div class="absolute top-0 z-0 w-full h-0.5 transform translate-y-2.5 translate-x-1 bg-gray-300 left-1/2" :class="{ 'bg-accent': isDone('captured', pickup.id) }"></div>
                        <div class="w-full mt-2">
                          Order Captured
                        </div>
                      </div>
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('assigned', pickup.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('assigned', pickup.id)" />
                        </div>
                        <div class="absolute top-0 z-0 w-full h-0.5 transform translate-y-2.5 translate-x-1 bg-gray-300 left-1/2" :class="{ 'bg-accent': isDone('assigned', pickup.id) }"></div>
                        <div class="w-full mt-2">
                          Driver Assigned
                        </div>
                      </div>
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('in-progress', pickup.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('in-progress', pickup.id)" />
                        </div>
                        <div class="absolute top-0 z-0 w-full h-0.5 transform translate-y-2.5 translate-x-1 bg-gray-300 left-1/2" :class="{ 'bg-accent': isDone('in-progress', pickup.id) }"></div>
                        <div class="w-full mt-2">
                          Driver En Route
                        </div>
                      </div>
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('complete', pickup.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('complete', pickup.id)" />
                        </div>
                        <div class="w-full mt-2">
                          Pickup Complete
                        </div>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Deliveries -->
      <div class="mt-8 border-b">
        <div class="px-6 pb-4 text-lg font-bold">
          Deliveries
        </div>
        <div>
          <template v-for="delivery in deliveries">
            <div :key="delivery.id" class="cursor-pointer hover:bg-gray-50">
              <button class="grid items-start justify-between w-full px-6 py-4 text-left border-t gap-y-2 lg:grid-cols-12"
              @click="openDetails(delivery)">
                <div class="lg:col-span-6">
                  <div>
                    <div class="max-w-full truncate">
                      <span class="font-medium" v-if="delivery.contact_name">{{ delivery.contact_name }}</span>  
                      <span v-if="delivery.name"> &mdash; {{ delivery.name }}</span>
                    </div>
                  </div>
                  <div v-if="delivery.formatted_address" class="max-w-full text-gray-600 truncate">
                    {{ delivery.formatted_address }}
                  </div>
                  <div class="mt-2 md:mt-1 md:flex md:space-x-4">
                    <div v-if="delivery.contact_phone" class="flex items-center">
                      <fa :icon="['fas', 'phone']" fixed-width class="mr-1 text-xs opacity-50" /> {{ delivery.contact_phone }}
                    </div>
                    <div v-if="delivery.contact_email" class="flex items-center">
                      <fa :icon="['fas', 'envelope']" fixed-width class="mr-1 text-xs opacity-50" /> {{ delivery.contact_email }}
                    </div>
                    <div v-if="delivery.reference" class="flex items-center">
                      <fa :icon="['fas', 'hashtag']" fixed-width class="mr-1 text-xs opacity-50" /> {{ delivery.reference }}
                    </div>
                  </div>
                </div>
                <div class="grid gap-2 text-gray-500 lg:col-span-4">
                  <div>
                    <div v-for="product in delivery.products" :key="product.id" class="truncate">
                      {{ product.quantity }}x {{ product.name}}
                    </div>
                  </div>
                  <div>
                    <div v-for="addon in delivery.addons" :key="addon.id" class="truncate">
                      &check; {{ addon.name}}
                    </div>
                  </div>
                </div>

                <div v-if="delivery.status" class="flex items-center justify-end lg:col-span-2">
                  <div>
                    <span class="inline-block px-3 py-1 text-xs font-medium tracking-wide uppercase border rounded-full" :class="statusClass(delivery.status)">
                      {{ delivery.status | capitalize }}
                    </span>
                  </div>
                  <div class="ml-6" v-if="delivery.status">
                    <fa :icon="['far', 'chevron-down']" class="transition duration-200 transform" :class="{ 'rotate-180': showDetails === delivery.id}" />
                  </div>
                </div>
              </button>
              <div>
                <Collapse :active="showDetails === delivery.id">
                  <div class="px-6 pb-4 space-y-4">
                    <div v-if="delivery.notes">
                      <span class="font-medium">Notes:</span> {{ delivery.notes || 'No Notes' }}
                    </div>
                    <div class="grid grid-cols-4 gap-4 p-6 text-sm font-medium leading-tight text-center rounded bg-gray-50">
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('captured', delivery.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('captured', delivery.id)" />
                        </div>
                        <div class="absolute top-0 z-0 w-full h-0.5 transform translate-y-2.5 translate-x-1 bg-gray-300 left-1/2" :class="{ 'bg-accent': isDone('captured', delivery.id) }"></div>
                        <div class="w-full mt-2">
                          Order Captured
                        </div>
                      </div>
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('assigned', delivery.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('assigned', delivery.id)" />
                        </div>
                        <div class="absolute top-0 z-0 w-full h-0.5 transform translate-y-2.5 translate-x-1 bg-gray-300 left-1/2" :class="{ 'bg-accent': isDone('assigned', delivery.id) }"></div>
                        <div class="w-full mt-2">
                          Driver Assigned
                        </div>
                      </div>
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('in-progress', delivery.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('in-progress', delivery.id)" />
                        </div>
                        <div class="absolute top-0 z-0 w-full h-0.5 transform translate-y-2.5 translate-x-1 bg-gray-300 left-1/2" :class="{ 'bg-accent': isDone('in-progress', delivery.id) }"></div>
                        <div class="w-full mt-2">
                          Driver En Route
                        </div>
                      </div>
                      <div class="relative">
                        <div class="relative z-10 grid w-6 h-6 mx-auto bg-white border-2 border-gray-300 rounded-full place-items-center" 
                        :class="{ 'bg-accent border-accent text-white': isDone('complete', delivery.id) }">
                          <fa :icon="['far', 'check']" v-if="isDone('complete', delivery.id)" />
                        </div>
                        <div class="w-full mt-2">
                          Delivery Complete
                        </div>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Action Bar -->
    <div class="sticky bottom-0 flex justify-end p-4 border-t h-17 bg-gray-50" v-if="booking.status === 'pending'">
      <button class="btn btn-outline btn-sm" v-submitting="submitting" @click="cancelBooking">
        Cancel Booking
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomerBookingView',

  created () {
    this.getBooking()
  },

  computed: {
    pickups () {
      if (this.booking.status === 'pending' || this.booking.status === 'cancelled') {
        return this.booking.origins
      } else if (this.booking.orders) {
        return this.booking.orders.filter(order => order.type === 'pickup')
      } else {
        return []
      }
    },

    deliveries () {
      if (this.booking.status === 'pending' || this.booking.status === 'cancelled') {
        return this.booking.destinations
      } else if (this.booking.orders) {
        return this.booking.orders.filter(order => order.type === 'delivery')
      } else {
        return []
      }
    }
  },

  data () {
    return {
      submitting: false,
      loading: true,
      booking: {},
      showDetails: null
    }
  },

  methods: {
    async getBooking () {
      try {
        const { data } = await this.$api.get(`portal/${this.$store.state.portal.subdomain}/customer/bookings/${this.$route.params.id}`)
        this.booking = data.booking
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async cancelBooking () {
      if (window.confirm('Are you sure you want to cancel this booking?')) {
        try {
          this.submitting = true
          const { data } = await this.$api.delete(`portal/${this.$store.state.portal.subdomain}/customer/bookings/${this.$route.params.id}`)
          this.booking = data.booking
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    },

    openDetails (order) {
      if (order.status) {
        this.showDetails = this.showDetails === order.id ? null : order.id
      }
    },

    statusClass (status) {
      if (status === 'captured') { 
        return `bg-gray-50 border-gray-500 text-gray-500`
      } else if (status === 'assigned') { 
        return `bg-blue-100 border-blue-600 text-blue-600`
      } else if (status === 'in-progress') { 
        return `bg-blue-100 border-blue-600 text-blue-600`
      } else if (status === 'complete') { 
        return `bg-emerald-100 border-emerald-600 text-emerald-600`
      } else if (status === 'rescheduled') { 
        return `bg-yellow-100 border-yellow-600 text-yellow-600`
      } else if (status === 'cancelled') { 
        return `bg-red-100 border-red-600 text-red-600`
      }
    },

    isDone (status, order) {
      if (status === 'captured') {
        return true
      } else if (status === 'assigned') {
        return order.status === 'complete' || order.status === 'in-progress' || order.status === 'assigned'
      } else if (status === 'in-progress') {
        return order.status === 'complete' || order.status === 'in-progress'
      } else if (status === 'complete') {
        return order.status === 'complete'
      }
    }
  }
}
</script>
